/**
 * Primitive color tokens - raw hex values extracted from the codebase
 * These are the foundational colors that semantic tokens reference
 */

export const primitiveColors = {
  // Grays
  gray1: '#F5F6FA', // Light background
  gray2: '#F3F6FA', // Chat bot background
  gray3: '#E9F0F7', // Secondary background
  gray4: '#FAFBFC', // Table/form backgrounds
  gray5: '#F7FAFD', // Data grid background
  gray6: '#F0F4FA', // Column headers
  gray7: '#BDBDBD', // Border/outline colors
  gray8: '#AAA', // Muted text
  gray9: '#BBB', // No signup text
  gray10: '#444', // Secondary text
  gray11: '#333', // Button hover / body text
  gray12: '#222', // Primary dark text
  gray13: '#666666', // Medium gray text
  gray14: '#6B7280', // Caption/icon gray
  gray15: '#6B6C6E', // Subheadline gray
  gray16: '#909192', // Footer text
  gray17: '#E0E0E0', // Light border
  gray18: '#CCCCCC', // Medium border
  gray19: '#B0B0B0', // Mid-tone border
  gray20: '#B3B3B3', // Disabled text
  gray21: '#F9FAFB', // Subtle surface
  gray22: '#1C1B1F', // Near-black (Material)
  gray23: '#f1f4f6', // Warm surface
  gray24: '#F5F5F5', // Neutral surface
  gray25: '#A0A0A0', // Input border default
  gray26: '#707070', // Input border hover
  gray27: '#f8f9fa', // Light surface variant
  gray28: '#617080', // Muted text variant
  gray29: '#D9D9D9', // Icon decorative fill
  gray30: '#1f2937', // Dark nav text
  gray31: '#767676', // Placeholder text (WCAG AA: 4.54:1 on white)
  gray32: '#7e7f88', // Dimmed text

  // Blues
  blue1: '#EDF5FC', // Light blue background
  blue2: '#5B6CFF', // Primary brand blue
  blue3: '#64A2F8', // Light blue (gradient start)
  blue4: '#1976D2', // Dark blue
  blue5: '#EEF4FB', // Chat bot message bg
  blue6: '#E3EEFA', // Chat bot message hover
  blue7: '#48A4FF', // Logo blue
  blue8: '#2196f3', // Material blue
  blue9: '#4a5be8', // Brand primary hover

  // Purples
  purple1: '#5F26F5', // Gradient middle
  purple2: '#E01CD5', // Gradient end
  purple3: '#C44FF2', // Nav gradient endpoint
  purple4: '#9c27b0', // Curated source icon
  purple5: '#3f51b5', // Indigo gradient
  purple6: '#F3ECFF', // Light purple info background

  // Greens
  green1: '#2e7d32', // Success / MUI green (copied/success state)
  green2: '#e8f5e9', // Light green success chip/badge background
  green3: '#4caf50', // Bright green — ballot "complete" status dot

  // Reds
  red1: '#E86167', // Accent red
  red2: '#FF2D2D', // Ballot preview text
  red3: '#FF4500', // Reddit orange
  red4: '#FF5363', // Logo red
  red5: '#ef4444', // Danger/delete
  red6: '#FF0000', // Hard error
  red7: '#FF6B9D', // Gradient border pink
  red8: '#dc2626', // Danger action hover
  red10: '#FFB3B3', // Error text on dark surfaces (8.4:1 on rockSlate)

  // Oranges
  orange1: '#FFA726', // Gradient border orange
  orange2: '#ff9800', // News source icon
  ballotpediaBlue: '#334AAB', // Ballotpedia logo blue (the "B")
  ballotpediaOrange: '#F0A236', // Ballotpedia logo orange (the "P")

  // Ambers
  amber1: '#F59E0B', // Amber warning/unsaved-changes indicator
  amber2: '#FFF8E1', // Light amber background (notice/warning banners)
  amber3: '#F9A825', // Amber warning banner border and icon

  // Logo
  logoPurple: '#6900FF', // VoteMate logo purple

  // Teals
  aqua1: '#96d2d2', // Light aqua accent

  // Whites
  white: '#FFFFFF',
  white2: '#fafcff', // Slightly off-white

  black: '#000000',
  rockSlate: '#0C2F37', // Dark teal-tinted surface used across chat + ballot
  navy1: '#0F172A', // Dark navy (save bar / dark overlays)

  // Transparent
  transparent: 'transparent',

  // Overlay
  overlay: 'rgba(255,255,255,0.8)',
  overlayDark: 'rgba(0,0,0,0.32)',
} as const;

// Gradient definitions
export const primitiveGradients = {
  accountAvatar: 'linear-gradient(135deg, #5B6CFF, #E86167)',
  primary: 'linear-gradient(90deg, #64A2F8 0%, #5F26F5 50%, #E86167 100%)',
  rainbow: 'linear-gradient(90deg, #64A2F8 0%, #5F26F5 33%, #E86167 66%, #E01CD5 100%)',
  borderGradient: 'linear-gradient(135deg, #5B6CFF, #FF6B9D, #FFA726)',
  borderGradientDisabled: 'linear-gradient(135deg, #E0E0E0, #E0E0E0)',
  navBorderGradient: 'linear-gradient(90deg, #64A2F8, #C44FF2)',
} as const;
